import { useDataProvider, useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { playChime } from "@/lib/chime";

import type { TaskCard } from "../types";
import { useRefreshFunnel } from "./hooks";

/** Done, later, tomorrow: the same actions for the tasks page and the corner pop-ups */
export const useTaskActions = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefreshFunnel();
  const queryClient = useQueryClient();
  const after = () => {
    refresh();
    queryClient.invalidateQueries({ queryKey: ["task_cards"] });
  };

  const done = async (task: Pick<TaskCard, "id">) => {
    try {
      await dataProvider.update("tasks", {
        id: task.id,
        data: { done_date: new Date().toISOString() },
        previousData: task,
      });
      playChime("done");
      after();
      return true;
    } catch {
      notify("ra.notification.http_error", { type: "error" });
      return false;
    }
  };

  const moveTo = async (task: Pick<TaskCard, "id">, due: Date) => {
    try {
      await dataProvider.update("tasks", {
        id: task.id,
        data: { due_date: due.toISOString() },
        previousData: task,
      });
      after();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  const later = (task: Pick<TaskCard, "id">, minutes: number) =>
    moveTo(task, new Date(Date.now() + minutes * 60_000));

  const tomorrow = (task: Pick<TaskCard, "id">) => {
    const due = new Date();
    due.setDate(due.getDate() + 1);
    due.setHours(10, 0, 0, 0);
    return moveTo(task, due);
  };

  return { done, later, tomorrow };
};
